/**
 * Printable goods receipt slip (GRN phase 1, 2026-10-21, decision g16): one A4 page per receipt for the paper
 * trail — what arrived, from whom, for which job or store, with signature lines for procurement and the receiving
 * department. Pure function: data in, a self-contained HTML document out (opened and printed like the MR print).
 */
export interface GrnSlipLine {
  no: number; code?: string | null; item: string; goesTo?: string | null; accepted: number; rejected: number;
  unit?: string | null; price?: number | null; outcome?: string | null;
}
export interface GrnSlipData {
  grnId: string; poId: string; path?: string | null; vendor: string; department?: string | null;
  receivedOn: string; receivedBy?: string | null; paidBy?: string | null; deliveryNote?: string | null; invoice?: string | null;
  notes?: string | null; lines: GrnSlipLine[];
}

export function buildGrnSlipHtml(g: GrnSlipData, opts: { printedAt: string; printedBy: string }): string {
  const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const n2 = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const q = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const day = new Date(g.receivedOn).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const value = (l: GrnSlipLine) => l.accepted * Number(l.price || 0);
  const total = g.lines.reduce((s, l) => s + value(l), 0);
  const perUnit = new Map<string, number>();
  g.lines.forEach((l) => perUnit.set(l.unit || '', (perUnit.get(l.unit || '') || 0) + l.accepted));
  const unitsText = Array.from(perUnit.entries()).map(([u, n]) => `${q(n)} ${u}`.trim()).join(' · ');
  const docs = [g.deliveryNote ? `DN ${g.deliveryNote}` : '', g.invoice ? `Invoice ${g.invoice}` : ''].filter(Boolean).join(' · ');

  const rows = g.lines.map((l) => `
    <tr>
      <td class="n">${l.no}</td>
      <td>${l.code ? `<span class="code">${esc(l.code)}</span> ` : ''}${esc(l.item)}${l.rejected > 0 ? `<div class="rej">${q(l.rejected)} ${esc(l.unit || '')} rejected${l.outcome ? ` · ${esc(l.outcome)}` : ''}</div>` : ''}</td>
      <td>${esc(l.goesTo || '')}</td>
      <td class="r">${q(l.accepted)} ${esc(l.unit || '')}</td>
      <td class="r">${l.price != null ? n2(Number(l.price)) : ''}</td>
      <td class="r">${n2(value(l))}</td>
    </tr>`).join('');

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(g.grnId)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  body { font: 12px/1.45 -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; color: #111; margin: 0; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 12px; }
  .brand { font: 600 18px Georgia, serif; letter-spacing: .5px; }
  h1 { font-size: 16px; margin: 0; text-align: right; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; margin-bottom: 14px; }
  .meta b { display: inline-block; min-width: 92px; color: #555; font-weight: 500; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-weight: 600; border-bottom: 1px solid #111; padding: 6px 4px; font-size: 11px; text-transform: uppercase; letter-spacing: .3px; }
  td { border-bottom: 1px solid #ddd; padding: 6px 4px; vertical-align: top; }
  tr { page-break-inside: avoid; }
  .r { text-align: right; white-space: nowrap; } .n { width: 22px; color: #555; }
  .code { font: 600 11px ui-monospace, Menlo, monospace; }
  .rej { color: #a8071a; font-size: 11px; margin-top: 2px; }
  .tot td { border-bottom: 0; border-top: 1px solid #111; font-weight: 600; }
  .sign { display: grid; grid-template-columns: 1fr 1fr 0.7fr; gap: 24px; margin-top: 36px; page-break-inside: avoid; }
  .sign div { border-top: 1px solid #111; padding-top: 4px; font-size: 11px; color: #333; }
  .foot { margin-top: 18px; font-size: 10px; color: #777; }
</style></head><body>
  <div class="head">
    <div class="brand">SHAHIRA LASHEEN</div>
    <div><h1>Goods receipt ${esc(g.grnId)}</h1><div style="text-align:right">${esc(g.poId)}${g.path ? ` · ${esc(g.path)}` : ''}</div></div>
  </div>
  <div class="meta">
    <div><b>Vendor</b>${esc(g.vendor)}</div>
    <div><b>Received on</b>${esc(day)}</div>
    <div><b>Department</b>${esc(g.department || '')}</div>
    <div><b>Received by</b>${esc(g.receivedBy || '')}</div>
    ${g.paidBy ? `<div><b>Paid by</b>${esc(g.paidBy)}</div>` : ''}
    ${docs ? `<div><b>Documents</b>${esc(docs)}</div>` : ''}
    ${g.notes ? `<div style="grid-column: 1 / -1"><b>Notes</b>${esc(g.notes)}</div>` : ''}
  </div>
  <table>
    <thead><tr><th>#</th><th>Item</th><th>For</th><th class="r">Qty</th><th class="r">Price</th><th class="r">Value</th></tr></thead>
    <tbody>${rows}
      <tr class="tot"><td></td><td>Total</td><td></td><td class="r">${esc(unitsText)}</td><td></td><td class="r">${n2(total)}</td></tr>
    </tbody>
  </table>
  <div class="sign">
    <div>Received by (procurement)<br>${esc(g.receivedBy || '')}</div>
    <div>Handed to (department)<br>&nbsp;</div>
    <div>Date<br>&nbsp;</div>
  </div>
  <div class="foot">Printed ${esc(opts.printedAt)} by ${esc(opts.printedBy)}</div>
</body></html>`;
}
